/*
 * <faq-accordion>
 * Height animation fallback for the FAQ accordions. Browsers with `interpolate-size` animate the
 * <details> in CSS (sections/faq.liquid), so this only runs where it is missing - Safari / iOS.
 *
 * The browser closes a <details name> group instantly, so the group name is moved to a data
 * attribute and the "one open at a time" behaviour is handled here instead.
 */
if (!customElements.get("faq-accordion")) {
  class FaqAccordion extends HTMLElement {
    static DURATION = 300;

    connectedCallback() {
      if (CSS.supports("interpolate-size", "allow-keywords")) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      this.items = [...this.querySelectorAll("details")];
      this.items.forEach((details) => {
        details.removeAttribute("name");
        details.querySelector("summary").addEventListener("click", this.onClick);
      });
    }

    disconnectedCallback() {
      this.items?.forEach((details) => {
        details.querySelector("summary").removeEventListener("click", this.onClick);
      });
    }

    onClick = (event) => {
      event.preventDefault();
      const details = event.currentTarget.parentElement;
      const isOpen = details.open && !details.classList.contains("is-closing");

      if (isOpen) {
        this.close(details);
      } else {
        this.items.forEach((item) => {
          if (item !== details && item.open) this.close(item);
        });
        this.open(details);
      }
    };

    open(details) {
      const startHeight = details.offsetHeight;
      details.classList.remove("is-closing");
      details.open = true;
      this.animate(details, startHeight, details.scrollHeight + this.borderHeight(details));
    }

    close(details) {
      const style = getComputedStyle(details);
      const endHeight =
        details.querySelector("summary").offsetHeight +
        parseFloat(style.paddingTop) +
        parseFloat(style.paddingBottom) +
        this.borderHeight(details);

      details.classList.add("is-closing");
      this.animate(details, details.offsetHeight, endHeight, () => {
        details.classList.remove("is-closing");
        details.open = false;
      });
    }

    animate(details, startHeight, endHeight, onFinish) {
      details.animation?.cancel();
      details.style.overflow = "hidden";

      details.animation = details.animate(
        { height: [`${startHeight}px`, `${endHeight}px`] },
        { duration: FaqAccordion.DURATION, easing: "ease" },
      );
      details.animation.onfinish = () => {
        details.animation = null;
        details.style.overflow = "";
        onFinish?.();
      };
    }

    borderHeight(details) {
      const style = getComputedStyle(details);
      return parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    }
  }

  customElements.define("faq-accordion", FaqAccordion);
}
